import React from 'react';
import type { EventDto, EventThemePreset } from './types';
import { isAnnouncement, toEventAlignment } from './types';
import { resolveThemePreset } from './themePresets';
import { resolveEventIcon } from './icons';
import { resolveEventSurface } from './surfaceContent';
import type { EventIconProps } from './icons';
import {
    formatEventDateShort,
    formatEventTime,
    formatEventWeekdayShort
} from './formatEventWindow';

export interface EventBannerContentProps {
    readonly event: EventDto;
    /** Reserves the right-hand room for the parent's absolutely-positioned action button. */
    readonly reserveActionSpace?: boolean;
    /** Reserves room at the LEFT edge for the rotation dots, which sit outside
     *  this content in a column of their own (dashboard's EventBannerStrip).
     *  Small by design: the dot column is anchored to the banner's own left
     *  edge and reaches only ~4px into this content box. */
    readonly reserveLeadingSpace?: boolean;
}

/** Literal classes, never interpolated - Tailwind's JIT only sees whole strings. */
function spacingClass(action: boolean, leading: boolean): string {
    if (action) return leading ? 'pl-3 pr-44' : 'pr-44';
    return leading ? 'pl-3' : '';
}

interface IconBadgeProps {
    readonly theme: EventThemePreset;
    readonly Icon: React.FC<EventIconProps>;
}

const IconBadge: React.FC<IconBadgeProps> = ({ theme, Icon }) => (
    <span
        className={`w-9 h-9 rounded-[10px] ${theme.accent} ${theme.accentText} flex items-center justify-center flex-shrink-0`}
        style={{ boxShadow: theme.iconShadow }}
    >
        <Icon className="w-[19px] h-[19px]" />
    </span>
);

interface BadgePillProps {
    readonly theme: EventThemePreset;
    readonly label: string;
}

const BadgePill: React.FC<BadgePillProps> = ({ theme, label }) => (
    <span
        className={`${theme.badgeBg} ${theme.badgeText} font-bold text-[9px] tracking-[0.06em] uppercase rounded-full px-2 py-0.5 flex-shrink-0`}
    >
        {label}
    </span>
);

// Pure presentation: icon + title + badgeTag + sub-line. The parent owns the
// action button, its positioning, and whether space is reserved for it.
// `alignment` picks the layout and `iconKey` the badge: they are independent,
// so a centered banner may carry an icon.
export const EventBannerContent: React.FC<EventBannerContentProps> = ({
    event,
    reserveActionSpace = true,
    reserveLeadingSpace = false
}) => {
    const theme = resolveThemePreset(event.themePreset);
    const Icon = resolveEventIcon(event.iconKey);
    const announcement = isAnnouncement(event);
    const centered = toEventAlignment(event.alignment) === 'center';
    const content = resolveEventSurface(event, 'banner');
    const subtitle = content.subtitle ?? '';

    const dateShort = formatEventDateShort(event.startsAt);
    const time = formatEventTime(event.startsAt);
    const reserved = spacingClass(reserveActionSpace, reserveLeadingSpace);

    if (centered) {
        return (
            <div className={`flex items-center justify-center gap-3 ${reserved}`}>
                {Icon && <IconBadge theme={theme} Icon={Icon} />}
                <div className="flex flex-col text-center min-w-0">
                    <span className="flex items-center justify-center gap-2">
                        <span className="text-sm font-bold tracking-wide">{content.title}</span>
                        {event.badgeTag && <BadgePill theme={theme} label={event.badgeTag} />}
                    </span>
                    <span className="text-xs font-normal opacity-90">
                        {announcement ? subtitle : `${dateShort}, ${time} - ${subtitle}`}
                    </span>
                </div>
            </div>
        );
    }

    const weekdayShort = formatEventWeekdayShort(event.startsAt);
    return (
        <div className={`flex items-center gap-3 ${reserved}`}>
            {Icon && <IconBadge theme={theme} Icon={Icon} />}
            <div className="flex flex-col gap-0.5 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-gray-900 tracking-tight">
                        {content.title}
                    </span>
                    {event.badgeTag && <BadgePill theme={theme} label={event.badgeTag} />}
                </div>
                <span className={`text-xs font-medium ${theme.subtitleText} truncate`}>
                    {!announcement && (
                        <>
                            <b className="text-gray-700 font-semibold">
                                {weekdayShort}, {dateShort} · {time}
                            </b>
                            {'. '}
                        </>
                    )}
                    {subtitle}
                </span>
            </div>
        </div>
    );
};

export default EventBannerContent;
